// ═══════════════════════════════════════════════════════════════════════
// Module 1 — Actions métier nommées (D-E4), sur un même modèle en deux temps :
//   1. saisie courte (changement demandé, date d'effet, motif) ;
//   2. vérification : situation actuelle → changement demandé → date d'effet → conséquences
//      principales (calculées par le serveur quand elles en dépendent), puis confirmation.
// Rien n'est enregistré avant « Confirmer » ; chaque action est datée, motivée et tracée.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, ArrowRight, AlertTriangle, CheckCircle2, FileSignature, LogOut, PauseCircle, PlayCircle, UserRound, Briefcase } from 'lucide-react';
import { Bouton, BandeauInfo, formaterDate } from '../../design/composants';

const aujourdhui = () => new Date().toISOString().slice(0, 10);
const fr = (d) => formaterDate(d) || 'non renseignée';

function Libelle({ texte, requis, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>
        {texte}{requis && <span style={{ color: 'var(--sg-danger)' }}> *</span>}
      </span>
      {children}
    </label>
  );
}

function Recapitulatif({ lignes }) {
  return (
    <dl style={{ margin: '0 0 14px', display: 'grid', gridTemplateColumns: 'minmax(130px, 32%) 1fr', gap: '10px 14px', fontSize: 13 }}>
      {lignes.filter(Boolean).map(([t, v]) => (
        <div key={t} style={{ display: 'contents' }}>
          <dt style={{ color: 'var(--sg-texte-3)' }}>{t}</dt>
          <dd style={{ margin: 0, color: 'var(--sg-texte)', fontWeight: 500 }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Cadre commun des actions (saisie, puis vérification). */
export function ModaleAction({ titre, Icone, sousTitre, etape, saisie, verification, consequences = [], avertissements = [], erreur, enCours, onVerifier, onRetour, onConfirmer, onFermer, libelleConfirmer = 'Confirmer', danger = false }) {
  useEffect(() => {
    const echap = (e) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', echap);
    return () => document.removeEventListener('keydown', echap);
  }, [onFermer]);
  return (
    <div role="dialog" aria-modal="true" aria-label={titre}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="rh-carte" style={{ width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0, fontFamily: 'var(--sg-police)' }}>
        <div className="rh-carte-entete" style={{ marginBottom: 6 }}>
          <h2 className="rh-carte-titre">{Icone && <Icone size={18} aria-hidden="true" />}{titre}</h2>
          <button type="button" className="rh-btn rh-btn-discret rh-btn-icone" onClick={onFermer} aria-label="Fermer"><X size={18} /></button>
        </div>
        {sousTitre && <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--sg-texte-3)' }}>{sousTitre}</p>}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, fontSize: 12, color: 'var(--sg-texte-3)' }} aria-hidden="true">
          <span style={{ fontWeight: etape === 'saisie' ? 600 : 400, color: etape === 'saisie' ? 'var(--sg-primaire)' : undefined }}>1. Saisie</span>
          <ArrowRight size={13} />
          <span style={{ fontWeight: etape === 'verification' ? 600 : 400, color: etape === 'verification' ? 'var(--sg-primaire)' : undefined }}>2. Vérification et confirmation</span>
        </div>
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {etape === 'saisie' ? saisie : (
          <>
            {verification}
            {consequences.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-texte-2)', marginBottom: 6 }}>Conséquences principales</div>
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontSize: 13 }}>
                  {consequences.map((c) => (
                    <li key={c} style={{ display: 'flex', gap: 8, padding: '4px 0' }}><CheckCircle2 size={15} color="var(--sg-primaire)" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />{c}</li>
                  ))}
                </ul>
              </div>
            )}
            {avertissements.map((a) => <BandeauInfo key={a} ton="alerte" titre="À vérifier avant de confirmer">{a}</BandeauInfo>)}
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {etape === 'saisie'
            ? <><Bouton onClick={onFermer}>Annuler</Bouton><Bouton variante="primaire" onClick={onVerifier} disabled={enCours}>{enCours ? 'Vérification…' : 'Vérifier'}</Bouton></>
            : <><Bouton onClick={onRetour} disabled={enCours}>Modifier la saisie</Bouton>
              <Bouton variante="primaire" onClick={onConfirmer} disabled={enCours}
                style={danger ? { background: 'var(--sg-danger)', borderColor: 'var(--sg-danger)' } : undefined}>{enCours ? 'Enregistrement…' : libelleConfirmer}</Bouton></>}
        </div>
      </div>
    </div>
  );
}

/** Logique commune : étape, erreur, envoi. */
function useAction({ onTermine }) {
  const [etape, setEtape] = useState('saisie');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const executer = async (fn, suite) => {
    setErreur(''); setEnCours(true);
    try { const r = await fn(); if (suite) suite(r); } catch (e) { setErreur(e.response?.data?.message || 'L\'opération n\'a pas pu être réalisée.'); } finally { setEnCours(false); }
  };
  return { etape, setEtape, erreur, setErreur, enCours, executer, terminer: (message) => onTermine(message) };
}

// ─── Modifier le contrat ─────────────────────────────────────────────────────
const NATURES = [
  { valeur: 'RENOUVELLEMENT', libelle: 'Renouvellement (prolonger un contrat à durée déterminée)' },
  { valeur: 'AVENANT', libelle: 'Avenant (modifier le contrat en cours)' },
  { valeur: 'CONVERSION', libelle: 'Changement de type de contrat (ex. passage en CDI)' },
];
export function ModaleContrat({ salarieId, contrat, nom, onFermer, onTermine }) {
  const a = useAction({ onTermine });
  const aDuree = ['CDD', 'Saisonnier', 'Stage'].includes(contrat?.type);
  const [f, setF] = useState({ nature: aDuree ? 'RENOUVELLEMENT' : 'AVENANT', type_contrat: 'CDI', date_effet: '', date_fin: '', motif: '' });
  const [apercu, setApercu] = useState(null);
  const corps = () => ({ ...f, type_contrat: f.nature === 'CONVERSION' ? f.type_contrat : undefined, date_effet: f.date_effet || undefined, date_fin: f.date_fin || undefined });
  const situation = contrat ? `${contrat.type}${contrat.fin ? ` jusqu'au ${fr(contrat.fin)}` : ' sans date de fin'} (depuis le ${fr(contrat.debut)})` : 'Aucun contrat en vigueur';
  return (
    <ModaleAction titre="Modifier le contrat" Icone={FileSignature} sousTitre={`${nom} — la situation actuelle est conservée dans l'historique.`}
      etape={a.etape} erreur={a.erreur} enCours={a.enCours} onFermer={onFermer} onRetour={() => a.setEtape('saisie')}
      consequences={apercu?.consequences || []} avertissements={apercu?.avertissements || []}
      onVerifier={() => a.executer(() => axios.post(`/api/salaries/${salarieId}/actions/contrat/apercu`, corps()), ({ data }) => { setApercu(data); a.setEtape('verification'); })}
      onConfirmer={() => a.executer(() => axios.post(`/api/salaries/${salarieId}/actions/contrat`, corps()), ({ data }) => a.terminer(data.message))}
      libelleConfirmer="Confirmer la modification"
      saisie={<>
        <Recapitulatif lignes={[['Contrat en vigueur', situation]]} />
        <Libelle texte="Nature de la modification" requis>
          <select className="rh-champ-saisie" value={f.nature} onChange={(e) => setF({ ...f, nature: e.target.value })}>
            {NATURES.filter((n) => n.valeur !== 'RENOUVELLEMENT' || aDuree).map((n) => <option key={n.valeur} value={n.valeur}>{n.libelle}</option>)}
          </select>
        </Libelle>
        {f.nature === 'CONVERSION' && (
          <Libelle texte="Nouveau type de contrat" requis>
            <select className="rh-champ-saisie" value={f.type_contrat} onChange={(e) => setF({ ...f, type_contrat: e.target.value })}>
              {['CDI', 'CDD', 'Saisonnier', 'Stage', 'Journalier'].filter((t) => t !== contrat?.type).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Libelle>
        )}
        <div className="rh-grille">
          <Libelle texte={f.nature === 'RENOUVELLEMENT' ? 'Date d\'effet (par défaut : lendemain de la fin actuelle)' : 'Date d\'effet'} requis={f.nature !== 'RENOUVELLEMENT'}>
            <input type="date" className="rh-champ-saisie" value={f.date_effet} onChange={(e) => setF({ ...f, date_effet: e.target.value })} />
          </Libelle>
          {!(f.nature === 'CONVERSION' && f.type_contrat === 'CDI') && !(f.nature === 'AVENANT' && contrat?.type === 'CDI') && (
            <Libelle texte={f.nature === 'RENOUVELLEMENT' ? 'Nouvelle date de fin' : 'Date de fin'} requis={f.nature === 'RENOUVELLEMENT'}>
              <input type="date" className="rh-champ-saisie" value={f.date_fin} onChange={(e) => setF({ ...f, date_fin: e.target.value })} />
            </Libelle>
          )}
        </div>
        <Libelle texte="Motif" requis><input className="rh-champ-saisie" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} placeholder="Ex. surcroît d'activité, titularisation…" /></Libelle>
      </>}
      verification={apercu && <Recapitulatif lignes={[
        ['Situation actuelle', situation],
        ['Changement demandé', `${NATURES.find((n) => n.valeur === f.nature).libelle.split(' (')[0]} : ${apercu.nouveau.type_contrat}${apercu.nouveau.date_fin ? ` jusqu'au ${fr(apercu.nouveau.date_fin)}` : ' sans date de fin'}`],
        ['Date d\'effet', fr(apercu.nouveau.date_debut)],
        ['Motif', apercu.nouveau.motif],
      ]} />}
    />
  );
}

// ─── Sortie ──────────────────────────────────────────────────────────────────
export function ModaleSortie({ salarieId, nom, motifs, peutLicencier, onFermer, onTermine }) {
  const a = useAction({ onTermine });
  const [f, setF] = useState({ date_sortie: '', motif_code: '', motif: '' });
  const [apercu, setApercu] = useState(null);
  const licenciementReserve = f.motif_code === 'LICENCIEMENT' && !peutLicencier;
  const precisionRequise = ['AUTRE', 'LICENCIEMENT'].includes(f.motif_code);
  return (
    <ModaleAction titre="Sortie du salarié" Icone={LogOut} danger sousTitre={`${nom} ne disparaît pas du système : son dossier et son historique restent consultables par les personnes habilitées.`}
      etape={a.etape} erreur={a.erreur} enCours={a.enCours} onFermer={onFermer} onRetour={() => a.setEtape('saisie')}
      consequences={apercu?.consequences || []}
      onVerifier={() => {
        if (!f.date_sortie || !f.motif_code) { a.setErreur('La date de sortie et le motif sont obligatoires.'); return; }
        a.executer(() => axios.post(`/api/salaries/${salarieId}/actions/sortie/apercu`, f), ({ data }) => { setApercu(data); a.setEtape('verification'); });
      }}
      onConfirmer={() => { if (licenciementReserve) { a.setErreur('La sortie pour licenciement est finalisée par le DRH ou le RH principal.'); return; } a.executer(() => axios.post(`/api/salaries/${salarieId}/actions/sortie`, f), ({ data }) => a.terminer(data.message)); }}
      libelleConfirmer={licenciementReserve ? 'Finalisation réservée au DRH / RH principal' : 'Confirmer la sortie'}
      saisie={<>
        <div className="rh-grille">
          <Libelle texte="Dernier jour travaillé" requis><input type="date" className="rh-champ-saisie" value={f.date_sortie} onChange={(e) => setF({ ...f, date_sortie: e.target.value })} /></Libelle>
          <Libelle texte="Motif de sortie" requis>
            <select className="rh-champ-saisie" value={f.motif_code} onChange={(e) => setF({ ...f, motif_code: e.target.value })}>
              <option value="">Choisir…</option>
              {Object.entries(motifs || {}).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Libelle>
        </div>
        <Libelle texte={f.motif_code === 'LICENCIEMENT' ? 'Motif réel du licenciement' : f.motif_code === 'AUTRE' ? 'Précision du motif' : 'Précision (facultative)'} requis={precisionRequise}>
          <input className="rh-champ-saisie" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} placeholder={f.motif_code === 'LICENCIEMENT' ? 'Motif tel que notifié au salarié' : 'Ex. lettre de démission du…'} />
        </Libelle>
        {licenciementReserve && <BandeauInfo ton="info" titre="Préparation possible, finalisation réservée">Vous pouvez préparer cette sortie et vérifier ses conséquences ; seuls le DRH et le RH principal peuvent l'enregistrer.</BandeauInfo>}
      </>}
      verification={<Recapitulatif lignes={[
        ['Situation actuelle', 'En activité'],
        ['Changement demandé', `Sortie — ${(motifs || {})[f.motif_code] || ''}${f.motif ? ` (${f.motif})` : ''}`],
        ['Dernier jour travaillé', fr(f.date_sortie)],
      ]} />}
    />
  );
}

// ─── Suspendre / réintégrer ─────────────────────────────────────────────────
export function ModaleStatut({ salarieId, nom, mode, motifs, onFermer, onTermine }) {
  const a = useAction({ onTermine });
  const suspendre = mode === 'suspension';
  const [f, setF] = useState({ date_effet: aujourdhui(), motif_code: '', motif: '', date_fin_prevue: '' });
  const futur = f.date_effet > aujourdhui();
  const consequences = suspendre
    ? [`Statut « Suspendu » ${futur ? `à partir du ${fr(f.date_effet)}` : 'dès l\'enregistrement'}.`, 'L\'accès à l\'application n\'est pas bloqué : c\'est une décision distincte (« Bloquer l\'accès », onglet Accès).', 'Le contrat, le poste et l\'historique sont conservés ; la réintégration rétablit le statut « Actif ».']
    : [`Statut « Actif » ${futur ? `à partir du ${fr(f.date_effet)}` : 'dès l\'enregistrement'}.`, 'Un éventuel blocage de l\'accès à l\'application reste à lever séparément (onglet Accès).'];
  return (
    <ModaleAction titre={suspendre ? 'Suspendre le salarié' : 'Réintégrer le salarié'} Icone={suspendre ? PauseCircle : PlayCircle} sousTitre={nom}
      etape={a.etape} erreur={a.erreur} enCours={a.enCours} onFermer={onFermer} onRetour={() => a.setEtape('saisie')} consequences={consequences}
      onVerifier={() => {
        if (!f.date_effet || (suspendre && !f.motif_code) || f.motif.trim().length < 3) { a.setErreur('La date, le motif et sa précision sont obligatoires.'); return; }
        a.setErreur(''); a.setEtape('verification');
      }}
      onConfirmer={() => a.executer(() => axios.post(`/api/salaries/${salarieId}/actions/${suspendre ? 'suspension' : 'reintegration'}`, f), ({ data }) => a.terminer(data.message))}
      libelleConfirmer={suspendre ? 'Confirmer la suspension' : 'Confirmer la réintégration'} danger={suspendre}
      saisie={<>
        <div className="rh-grille">
          <Libelle texte="Date d'effet" requis><input type="date" className="rh-champ-saisie" value={f.date_effet} onChange={(e) => setF({ ...f, date_effet: e.target.value })} /></Libelle>
          {suspendre && <Libelle texte="Reprise prévue (facultatif)"><input type="date" className="rh-champ-saisie" value={f.date_fin_prevue} onChange={(e) => setF({ ...f, date_fin_prevue: e.target.value })} /></Libelle>}
        </div>
        {suspendre && (
          <Libelle texte="Motif" requis>
            <select className="rh-champ-saisie" value={f.motif_code} onChange={(e) => setF({ ...f, motif_code: e.target.value })}>
              <option value="">Choisir…</option>
              {Object.entries(motifs || {}).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Libelle>
        )}
        <Libelle texte={suspendre ? 'Précision du motif' : 'Motif de la réintégration'} requis><input className="rh-champ-saisie" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} /></Libelle>
      </>}
      verification={<Recapitulatif lignes={[
        ['Situation actuelle', suspendre ? 'Actif' : 'Suspendu'],
        ['Changement demandé', suspendre ? `Suspension — ${(motifs || {})[f.motif_code] || ''}` : 'Réintégration'],
        ['Date d\'effet', fr(f.date_effet)],
        ['Motif', f.motif],
      ]} />}
    />
  );
}

// ─── Changer de responsable ─────────────────────────────────────────────────
export function ModaleResponsable({ salarieId, nom, responsable, onFermer, onTermine }) {
  const a = useAction({ onTermine });
  const [postes, setPostes] = useState([]);
  const [f, setF] = useState({ poste_manager_id: '', date_effet: aujourdhui(), motif: '' });
  useEffect(() => { axios.get('/api/postes').then(({ data }) => setPostes((data.postes || []).filter((p) => p.actif !== false))).catch(() => setPostes([])); }, []);
  const choisi = postes.find((p) => p.id === f.poste_manager_id);
  const actuel = responsable?.etat === 'defini' ? `${responsable.prenom} ${(responsable.nom || '').toUpperCase()} (${responsable.poste_responsable || responsable.intitule || 'poste'})`
    : responsable?.etat === 'vacant' ? `Poste vacant (${responsable.intitule})` : 'Responsable à définir';
  return (
    <ModaleAction titre="Changer de responsable" Icone={UserRound} sousTitre={nom}
      etape={a.etape} erreur={a.erreur} enCours={a.enCours} onFermer={onFermer} onRetour={() => a.setEtape('saisie')}
      consequences={[
        choisi ? (choisi.titulaire_nom ? `${choisi.titulaire_nom} devient le responsable : il ou elle reçoit les demandes du salarié (congés…).` : `Le poste « ${choisi.intitule} » est vacant : les demandes du salarié iront au service RH tant qu'il n'est pas pourvu.`)
          : 'Le salarié n\'aura plus de responsable : ses demandes iront au service RH.',
        'Le changement est daté et conservé dans l\'historique.',
      ]}
      onVerifier={() => { if (f.motif.trim().length < 3) { a.setErreur('Le motif est obligatoire.'); return; } a.setErreur(''); a.setEtape('verification'); }}
      onConfirmer={() => a.executer(() => axios.post(`/api/salaries/${salarieId}/actions/responsable`, { ...f, poste_manager_id: f.poste_manager_id || null }), ({ data }) => a.terminer(data.message))}
      libelleConfirmer="Confirmer le changement"
      saisie={<>
        <Recapitulatif lignes={[['Responsable actuel', actuel]]} />
        <Libelle texte="Nouveau responsable (poste)">
          <select className="rh-champ-saisie" value={f.poste_manager_id} onChange={(e) => setF({ ...f, poste_manager_id: e.target.value })}>
            <option value="">Aucun responsable</option>
            {postes.map((p) => <option key={p.id} value={p.id}>{p.intitule} — {p.titulaire_nom || 'vacant'}</option>)}
          </select>
        </Libelle>
        <div className="rh-grille">
          <Libelle texte="Date d'effet" requis><input type="date" className="rh-champ-saisie" value={f.date_effet} onChange={(e) => setF({ ...f, date_effet: e.target.value })} /></Libelle>
          <Libelle texte="Motif" requis><input className="rh-champ-saisie" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} /></Libelle>
        </div>
      </>}
      verification={<Recapitulatif lignes={[
        ['Situation actuelle', actuel],
        ['Changement demandé', choisi ? `${choisi.titulaire_nom || 'Poste vacant'} (${choisi.intitule})` : 'Aucun responsable'],
        ['Date d\'effet', fr(f.date_effet)],
        ['Motif', f.motif],
      ]} />}
    />
  );
}

// ─── Changer de métier ───────────────────────────────────────────────────────
export function ModaleMetier({ salarieId, nom, metier, onFermer, onTermine }) {
  const a = useAction({ onTermine });
  const [metiers, setMetiers] = useState([]);
  const [f, setF] = useState({ metier_id: '', date_effet: aujourdhui(), motif: '' });
  useEffect(() => { axios.get('/api/metiers', { params: { actifs: '1' } }).then(({ data }) => setMetiers(data.metiers || [])).catch(() => setMetiers([])); }, []);
  const choisi = metiers.find((m) => m.id === f.metier_id);
  return (
    <ModaleAction titre="Changer de métier" Icone={Briefcase} sousTitre={nom}
      etape={a.etape} erreur={a.erreur} enCours={a.enCours} onFermer={onFermer} onRetour={() => a.setEtape('saisie')}
      consequences={['Le nouveau métier s\'affiche sur la fiche et dans la liste.', 'L\'ancien métier reste visible dans l\'historique et dans « Situation au… ».']}
      onVerifier={() => { if (!f.metier_id || f.motif.trim().length < 3) { a.setErreur('Le métier et le motif sont obligatoires.'); return; } a.setErreur(''); a.setEtape('verification'); }}
      onConfirmer={() => a.executer(() => axios.post(`/api/salaries/${salarieId}/actions/metier`, f), ({ data }) => a.terminer(data.message))}
      libelleConfirmer="Confirmer le changement"
      saisie={<>
        <Recapitulatif lignes={[['Métier actuel', metier?.libelle || 'Non renseigné']]} />
        {metiers.length === 0 && <BandeauInfo ton="info">Aucun métier n'est encore défini. Créez-les dans « Métiers » (liste des employés).</BandeauInfo>}
        <Libelle texte="Nouveau métier" requis>
          <select className="rh-champ-saisie" value={f.metier_id} onChange={(e) => setF({ ...f, metier_id: e.target.value })}>
            <option value="">Choisir…</option>
            {metiers.filter((m) => m.id !== metier?.id).map((m) => <option key={m.id} value={m.id}>{m.libelle}</option>)}
          </select>
        </Libelle>
        <div className="rh-grille">
          <Libelle texte="Date d'effet" requis><input type="date" className="rh-champ-saisie" value={f.date_effet} onChange={(e) => setF({ ...f, date_effet: e.target.value })} /></Libelle>
          <Libelle texte="Motif" requis><input className="rh-champ-saisie" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} placeholder="Ex. reconversion, erreur de classement…" /></Libelle>
        </div>
      </>}
      verification={<Recapitulatif lignes={[
        ['Situation actuelle', metier?.libelle || 'Non renseigné'],
        ['Changement demandé', choisi?.libelle],
        ['Date d\'effet', fr(f.date_effet)],
        ['Motif', f.motif],
      ]} />}
    />
  );
}

/** Annulation d'un événement saisi par erreur (motif obligatoire). */
export function ModaleAnnulation({ titre, description, url, onFermer, onTermine }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  return (
    <ModaleAction titre={titre} Icone={AlertTriangle} sousTitre={description} etape="verification" erreur={erreur} enCours={enCours} onFermer={onFermer} onRetour={onFermer}
      danger libelleConfirmer="Confirmer l'annulation"
      consequences={['L\'événement reste visible dans l\'historique, marqué « annulé », avec votre nom et le motif.', 'Ses effets sont défaits (statut, contrat, poste ou responsable selon le cas).']}
      onConfirmer={async () => {
        if (motif.trim().length < 3) { setErreur('Le motif de l\'annulation est obligatoire.'); return; }
        setEnCours(true); setErreur('');
        try { const { data } = await axios.post(url, { motif }); onTermine([data.message, ...(data.avertissements || [])].join(' ')); }
        catch (e) { setErreur(e.response?.data?.message || 'L\'annulation n\'a pas pu être réalisée.'); }
        finally { setEnCours(false); }
      }}
      verification={<Libelle texte="Motif de l'annulation" requis><input className="rh-champ-saisie" value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. saisie sur le mauvais salarié" /></Libelle>}
    />
  );
}
